import React, { useEffect, useState } from "react";
import { useHistory } from "react-router";
import { useTranslation } from "react-i18next";
import AppManager from "../../managers/AppManager";
import RouteManager from "../../managers/RouteManager";
import {
    DKLabel,
    DKButton,
    DKInput,
    DKIcon,
    INPUT_TYPE,
    INPUT_VIEW_DIRECTION,
    showToast,
    TOAST_TYPE,
    showLoader,
    removeLoader
} from "deskera-ui-library";
import MessageInfo from "../common/MessageInfo";
import ic_delete from "../../assets/icons/ic_delete.png";
import { removeDelayedLoader, showDelayedLoader } from "../../utility/Utility";
import PerformaceService from "../../services/PerformaceService";
import moment from "moment";
import { DATE_FORMAT } from "../../constants/Constant";
import { t } from "i18next";
import AppraisalManager from "./AppraisalManager";

const APPRAISAL_FRQUENCY = [
    { value: "QUARTERLY", label: t("THREE_MONTHS") },
    { value: "HALF_YEARLY", label: t("SIX_MONTHS") },
    { value: "YEARLY", label: t("TWELVE_MONTHS") }
];

function AppraisalCycle(props) {
    const history = useHistory();
    const { t } = useTranslation();

    const initialFieldState = {
        dueDate: {
            id: 'dueDate',
            title: t("APPRAISAL_DUE"),
            type: INPUT_TYPE.DATE,
            validation: [["required"], ["minDate", new Date()]],
        },
        frequency: {
            id: 'frequency',
            title: t("APPRAISAL_PERIOD_FRQUENCY"),
            type: INPUT_TYPE.DROPDOWN,
            value: APPRAISAL_FRQUENCY[0].label,
            options: APPRAISAL_FRQUENCY,
            validation: [["required"]]
        },
        questions: []
    }

    const [field, setField] = useState(initialFieldState);
    const [activeCycle, setActiveCycle] = useState(null);
    const [questionsLocked, setQuestionsLocked] = useState(false);

    useEffect(() => {
        RouteManager.setPresenter({ props: { ...props, history: history } });
        AppManager.scrollToTop();
        if(AppraisalManager.getAppraisalData()) {
            loadAppraisalCycle(AppraisalManager.getAppraisalData());
        } else {
            getAppraisalCycle();
        }
    }, []);

    const getDefaultQuestion = (index) => {
        return {
            id: `question${index}`,
            title: `${t("QUESTION")} ${index}`,
            type: INPUT_TYPE.LONG_TEXT,
            showValueLength: true,
            validation: [["required"], ["maxLength", 300]]
        }
    }

    const pushDefaultQuestions = () => {
        const questions = [...field.questions];

        for (let i = 0; i < 3; i++) {
            questions.push(getDefaultQuestion(questions.length + 1));
        }

        setField({
            ...field,
            questions: questions
        });
    }

    const lockUnlockQuestions = (dueDate) => {
        setQuestionsLocked(moment().add(30, "day").isAfter(moment(dueDate)));
    }

    const getAppraisalCycle = () => {
        const timeout = showDelayedLoader();
        PerformaceService.getAppraisalCycle().then(
            (response) => {
                removeDelayedLoader(timeout);
                if (response?.length > 0) {
                    const activeCycle = response.find((c) => c.active);
                    if (activeCycle) {
                        setActiveCycle(activeCycle);
                        setAppraisalCycleValue(activeCycle);
                    } else {
                        pushDefaultQuestions();
                    }
                } else {
                    pushDefaultQuestions();
                }
            }, (error) => {
                removeDelayedLoader(timeout);
            }
        );
    }

    const loadAppraisalCycle = (activeCycle) => {
        if (activeCycle) {
            setActiveCycle(activeCycle);
            setAppraisalCycleValue(activeCycle);
        } else {
            pushDefaultQuestions();
        }
    }

    const setAppraisalCycleValue = (activeCycle) => {
        const state = {...field};

        const endDate = moment(activeCycle.endDate, DATE_FORMAT.IND);
        if (activeCycle.endDate && endDate.isValid()) {
            state.dueDate.value = endDate.toDate();
            lockUnlockQuestions(endDate);
        }

        state.frequency.value = getDropdownLabel(activeCycle.frequency, APPRAISAL_FRQUENCY);

        if (activeCycle.questions?.length > 0) {
            const sortedQuestions = activeCycle.questions.sort((q1, q2) => q1.seq - q2.seq);
            state.questions = sortedQuestions.map((question, index) => {
                    return {
                        ...getDefaultQuestion(index + 1),
                        value: question.question
                    };
                }
            );
        }

        setField(state);
    }

    const isRequired = (validation) => {
        return validation ? validation.findIndex((v) => v[0] === "required") !== -1 : false;
    }

    const showError = (errorMessage) => {
        if (errorMessage) {
            return (
                <DKLabel
                    className="p-h-xl ml-xs fs-s text-red"
                    text={errorMessage}
                />
            )
        }
    }

    const getMaxLengthValue = (validation) => {
        return validation ? validation.find((v) => v[0] === "maxLength")?.[1] : false;
    }

    const showValueLength = (field) => {
        return (
            <DKLabel
                className="p-h-xl ml-auto"
                text={`${field.value?.length || 0}/${getMaxLengthValue(field.validation)}`}
            />
        );
    }

    const hasError = (field, value) => {
        let error = {
            invalid: false,
            errorMessage: null
        };

        const validation = field.validation;

        if (validation && validation.length !== 0) {
            validation.forEach((validation) => {
                switch (validation[0]) {
                    case "required":
                        if (value === null || value === undefined || String(value).trim() === "") {
                            error.invalid = true;
                        }
                        break;
                    case "maxLength":
                        if (value?.length > validation[1]) {
                            error.invalid = true;
                            error.errorMessage = t("MAXLENGTH_EXCEEDS");
                        }
                        break;
                    case "minDate":
                        if (moment(value).startOf("day").isBefore(moment(validation[1]).startOf("day"))) {
                            error.invalid = true;
                            error.errorMessage = t("APPRAISAL_DUE_PASTE_DATE");
                        }
                        break;
                    default:
                        error.invalid = false;
                        error.errorMessage = null;
                        break;
                }
            });
        }

        return error;
    }

    const updateState = (stateName, f, v) => {
        const state = {...field};

        if (Array.isArray(state[stateName])) {
            state[stateName].forEach((q) => {
                if (q.id === f.id) {
                    const error = hasError(f, v);
                    q.invalid = error.invalid;
                    q.errorMessage = error.errorMessage;
                    q.value = v;
                }
            });
        } else {
            state[stateName] = {...f, value: v, ...hasError(f, v)};
        }

        if (stateName === "dueDate") {
            lockUnlockQuestions(v);
        }

        setField(state);
    }

    const pushQuestion = () => {
        const questions = [...field.questions];
        questions.push(getDefaultQuestion(questions.length + 1));

        setField({
            ...field,
            questions: questions
        });
    }

    const popQuestion = (question) => {
        const questions = [...field.questions].filter((q) => q.id !== question.id);
        setField({
            ...field,
            questions: questions
        });
    }

    const renderField = (stateName, field, width) => {
        switch (field.type) {
            case INPUT_TYPE.DATE:
                return (
                    <div key={field.id} style={{ flex: "0 0 50%", maxWidth: width }}>
                        <div className="row pb-l p-h-xl position-relative">
                            <DKInput
                                title={field.title}
                                type={field.type}
                                direction={INPUT_VIEW_DIRECTION.VERTICAL}
                                value={field.value}
                                errorMessage={field.errorMessage}
                                readOnly={!!field.readOnly}
                                required={isRequired(field.validation)}
                                invalid={field.invalid}
                                canValidate={field.invalid}
                                onChange={(value) => {
                                    updateState(stateName, field, value);
                                }}
                            />
                        </div>
                        {field.invalid && <div className="row" style={{ marginTop: "-12px" }}>
                            {showError(field.errorMessage)}
                        </div>}
                    </div>
                );
            case INPUT_TYPE.LONG_TEXT:
                return (
                    <div key={field.id} style={{ flex: "0 0 50%", maxWidth: width }}>
                        <div className="row pb-xl p-h-xl position-relative ">
                            <DKInput
                                title={field.title}
                                type={field.type}
                                direction={INPUT_VIEW_DIRECTION.VERTICAL}
                                value={field.value}
                                errorMessage={field.errorMessage}
                                readOnly={!!field.readOnly || questionsLocked}
                                required={isRequired(field.validation)}
                                invalid={field.invalid}
                                canValidate={field.invalid}
                                onChange={(value) => {
                                    updateState(stateName, field, value);
                                }}
                            />
                        </div>
                        <div className="row" style={{ marginTop: "-20px", marginBottom: "9px" }}>
                            {field.invalid && showError(field.errorMessage)}
                            {field.showValueLength && showValueLength(field)}
                        </div>
                    </div>
                );
            case INPUT_TYPE.DROPDOWN:
                return (
                    <div key={field.id} style={{ flex: "0 0 50%", maxWidth: width }}>
                        <div className="row pb-l p-h-xl position-relative">
                            <DKInput
                                title={field.title}
                                type={INPUT_TYPE.DROPDOWN}
                                direction={INPUT_VIEW_DIRECTION.VERTICAL}
                                dropdownConfig={{
                                    data: field.options,
                                    renderer: (i, item) => item.label,
                                    onSelect: (i, value) => {
                                        updateState(stateName, field, value.label);
                                    },
                                }}
                                value={field.value}
                                errorMessage={field.errorMessage}
                                readOnly={!!field.readOnly}
                                required={isRequired(field.validation)}
                                invalid={field.invalid}
                                canValidate={field.invalid}
                                onChange={() => { }}
                            />
                        </div>
                    </div>
                );
            default:
                break;
        }
    }

    const renderQuestionField = (field) => {
        return (
            <div className="row align-items-start">
                {renderField("questions", field, "50%")}
                <DKIcon
                    src={ic_delete}
                    className={getDisableCSS("ic-s-2 cursor-hand mt-l", true)}
                    onClick={() => {
                        popQuestion(field);
                    }}
                />
            </div>
        )
    }

    const getDropdownLabel = (v, o) => {
        let value = v;

        const index = o.findIndex(i => i.value === v);
        if (index !== -1) {
            value = o[index].label;
        }

        return value;
    }

    const getDropdownValue = (v, o) => {
        let value = v;

        const index = o.findIndex(i => i.label === v);
        if (index !== -1) {
            value = o[index].value;
        }

        return value;
    }

    const isInvalid = () => {
        let invalid = false;

        const f = {...field};
        Object.entries(f).forEach(([key, field]) => {
            if (Array.isArray(field)) {
                field.forEach((question) => {
                    const error = hasError(question, question.value);
                    question.invalid = error.invalid;
                    question.errorMessage = error.errorMessage;

                    if (question.invalid) {
                        invalid = true;
                    }
                });
            } else {
                const error = hasError(field, field.value);
                field.invalid = error.invalid;
                field.errorMessage = error.errorMessage;

                if (field.invalid) {
                    invalid = true;
                }
            }
        });

        setField(f);

        return invalid;
    }

    const getPayload = () => {
        const payload = {
            dueDate: moment(field.dueDate.value).format(DATE_FORMAT.IND),
            frequency: getDropdownValue(field.frequency.value, APPRAISAL_FRQUENCY),
            questions: field.questions.map((q) => q.value)
        }

        return payload;
    }

    const saveAppraisalCycle = () => {
        if (isInvalid()) {
            return;
        }

        const payload = getPayload();

        if (activeCycle) {
            showLoader(t("UPDATING_PERFORMANCE_SETTINGS"));
            PerformaceService.updateAppraisalCycle(activeCycle.id, payload).then(
                (response) => {
                    removeLoader();
                    showToast(t("UPDATE_PERFORMANCE_SETTINGS_SUCCESS"), TOAST_TYPE.SUCCESS);
                }, (error) => {
                    removeLoader();
                    if (error?.errorMessage) {
                        showToast(error.errorMessage, TOAST_TYPE.FAILURE);
                    } else {
                        showToast(t("INTERNAL_SERVER_ERROR"), TOAST_TYPE.FAILURE);
                    }
                }
            );
        } else {
            showLoader(t("SAVING_PERFORMANCE_SETTINGS"));
            PerformaceService.saveAppraisalCycle(payload).then(
                (response) => {
                    removeLoader();
                    showToast(t("SAVE_PERFORMANCE_SETTINGS_SUCCESS"), TOAST_TYPE.SUCCESS);
                    RouteManager.navigateToHome();
                }, (error) => {
                    removeLoader();
                    if (error?.errorMessage) {
                        showToast(error.errorMessage, TOAST_TYPE.FAILURE);
                    } else {
                        showToast(t("INTERNAL_SERVER_ERROR"), TOAST_TYPE.FAILURE);
                    }
                    
                }
            );
        }
    }

    const getDisableCSS = (initial, isButton) => {
        let CSSClass = initial || "";

        if (questionsLocked) {
            CSSClass += isButton ? " disable-button" : " disable-section";
        }

        return CSSClass;
    }

    return (
        <div className="parent-width">
            <div className="row">
                <DKLabel text={t("PERFORMANCE")} className="fs-l fw-b" />
                <div className="ml-auto d-flex">
                    <DKButton
                        title={t("CANCEL")}
                        className="border-radius-m bg-gray1 border-m"
                        onClick={() => {
                            RouteManager.navigateToHome();
                        }}
                    />
                    <DKButton
                        title={t("SAVE")}
                        className="bg-app text-white ml-m"
                        onClick={() => {
                            saveAppraisalCycle();
                        }}
                    />
                </div>
            </div>
            <div className="bg-white border-radius-m p-v-xl m-v-m">
                <DKLabel text={t("APPRAISAL_CYCLE_SCHEDULE")} className="fs-m fw-b p-h-xl mb-l" />
                <div className="row">
                    {renderField("dueDate", field.dueDate, "30%")}
                    {renderField("frequency", field.frequency, "30%")}
                </div>
            </div>
            <div className="bg-white border-radius-m p-v-xl m-v-m">
                <DKLabel text={t("SELF_AND_MANAGER_ASSESSMENT")} className="fs-m fw-b p-h-xl" />
                <DKLabel text={t("SELF_ASSESSMENT_TEXT")} className="fs-s fw-m text-gray p-h-xl mb-xl" />
                <MessageInfo
                    message={[{ message: t("SELF_ASSESSMENT_TEXT1") }]}
                    bannerClass="p-h-xl mb-xl"
                    isClosable={true}
                />
                <div>
                    {field.questions.map((question) => renderQuestionField(question))}
                </div>
                <div className="mt-s d-flex">
                    <DKButton
                        title={`+ ${t("QUESTION")}`}
                        className={getDisableCSS("m-h-xl bg-app text-white", true)}
                        disabled={questionsLocked}
                        onClick={() => {
                            pushQuestion();
                        }}
                    />
                </div>
            </div>
        </div>
    );
}

export default AppraisalCycle;
